import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const next = params.get("next");
      if (next) navigate(next);
      else if (u.role === "facility") navigate("/dashboard/facility");
      else navigate("/dashboard/caregiver");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      toast.error("Could not sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="mx-auto max-w-md px-6 py-16">
      <Card className="rounded-2xl border border-border/60 bg-card p-8 shadow-sm">
        <h1 className="font-serif text-3xl text-forest">Welcome back</h1>
        <p className="mt-1 text-sm text-muted-foreground">Sign in to manage inquiries or your facility.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Email</label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs font-medium text-muted-foreground">Password</label>
              <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="mt-1 h-11"
            />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full rounded-full bg-primary py-6 text-primary-foreground"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="mt-5 text-sm text-muted-foreground">
          New to Providence?{" "}
          <Link to="/register" className="font-medium text-primary hover:underline" data-testid="login-register-link">
            Create an account
          </Link>
        </p>
      </Card>
    </div>
  );
}
